/**
 * English: Layout context — manages sidebar open (mobile drawer) and collapsed (desktop icon-only) state.
 * Thai: Context จัดการ sidebar เปิด (drawer mobile) และ ยุบ (desktop icon-only)
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface LayoutContextValue {
  /** Mobile drawer open state */
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** Desktop collapsed (icon-only w-16) vs expanded (w-56) — legacy click toggle */
  isSidebarCollapsed: boolean;
  toggleSidebarCollapse: () => void;
  /** Desktop: keep nav bar expanded without hover */
  isSidebarPinned: boolean;
  toggleSidebarPinned: () => void;
  /** Desktop: pointer inside sidebar hover zone */
  isSidebarHovered: boolean;
  setSidebarHovered: (hovered: boolean) => void;
}

const LayoutContext = createContext<LayoutContextValue>({
  isSidebarOpen: false,
  setSidebarOpen: () => {},
  isSidebarCollapsed: false,
  toggleSidebarCollapse: () => {},
  isSidebarPinned: false,
  toggleSidebarPinned: () => {},
  isSidebarHovered: false,
  setSidebarHovered: () => {},
});

const COLLAPSE_KEY = 'sidebar_collapsed';
const PIN_KEY = 'sidebar_pinned';

export function LayoutProvider({ children }: { children: React.ReactNode }) {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });
  const [isSidebarPinned, setPinned] = useState(() => {
    return localStorage.getItem(PIN_KEY) === '1';
  });
  const [isSidebarHovered, setSidebarHovered] = useState(false);

  const toggleSidebarCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  const toggleSidebarPinned = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      localStorage.setItem(PIN_KEY, next ? '1' : '0');
      return next;
    });
  }, []);

  // Close mobile drawer on route change (resize above md)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => { if (mq.matches) setSidebarOpen(false); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <LayoutContext.Provider
      value={{
        isSidebarOpen,
        setSidebarOpen,
        isSidebarCollapsed,
        toggleSidebarCollapse,
        isSidebarPinned,
        toggleSidebarPinned,
        isSidebarHovered,
        setSidebarHovered,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  return useContext(LayoutContext);
}
